import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";
import type { Patient } from "@/store/profile";
import { PatientListPickerContent } from "@/components/account/PatientListPickerContent";

type PortalAccountMenuProps = {
  className?: string;
  onSignOut: () => void;
  patients: Patient[] | null | undefined;
  currentPatient: Patient | null;
  onPatientSelect: (patient: Patient) => void;
  /** Signed-in user's initials for the trigger avatar; falls back to a user icon. */
  initials?: string;
};

type Step = "actions" | "patients";

/**
 * Header account button: opens Switch patient and Log out (same patient list as login,
 * not a popover). A bottom sheet below md, a centred dialog from md up.
 */
export function PortalAccountMenu({
  className,
  onSignOut,
  patients,
  currentPatient,
  onPatientSelect,
  initials,
}: PortalAccountMenuProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("actions");
  const firstPatientButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const multi = (patients?.length ?? 0) > 1;
  const currentPatientId = currentPatient
    ? String(currentPatient.patientID ?? currentPatient.id)
    : "";

  const close = useCallback(() => {
    setOpen(false);
    setStep("actions");
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  /** Move focus into the dialog on open and back to the trigger on close. */
  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    closeButtonRef.current?.focus();
    return () => trigger?.focus();
  }, [open]);

  useEffect(() => {
    if (open && step === "patients") {
      firstPatientButtonRef.current?.focus();
    }
  }, [open, step]);

  const selectPatient = (p: Patient) => {
    onPatientSelect(p);
    close();
  };

  const handleOpen = () => {
    setStep("actions");
    setOpen(true);
  };

  const modal =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-end justify-center bg-healthsource-dark/60 duration-200 animate-in fade-in motion-reduce:animate-none md:items-center md:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-menu-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div
              className={cn(
                "relative max-h-[85dvh] w-full min-w-0 overflow-y-auto overscroll-contain break-words bg-white shadow-[0_12px_32px_rgba(30,37,43,0.14)] duration-200 animate-in motion-reduce:animate-none",
                /* Phone: bottom sheet clear of the home indicator. */
                "rounded-t-2xl px-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-2 slide-in-from-bottom-full",
                /* md+: centred dialog. */
                "md:max-w-xl md:rounded-xl md:border md:border-[#D5DBE4] md:p-4 md:fade-in md:zoom-in-95 md:slide-in-from-bottom-0"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#D5DBE4] md:hidden"
                aria-hidden
              />
              {/* One header row: title (or Back) on the left, close on the right inside the 16px gutter. */}
              <div className="flex items-start justify-between gap-2">
                {step === "actions" ? (
                  <div className="min-w-0 pt-1.5">
                    <h2
                      id="account-menu-title"
                      className="text-xl font-extrabold text-healthsource-dark"
                    >
                      Account
                    </h2>
                    <p className="mt-1 text-sm font-semibold text-healthsource-gray">
                      Choose an option
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="-ml-2 flex min-h-11 items-center gap-1.5 px-2 text-sm font-bold text-healthsource-navy hover:underline"
                    onClick={() => setStep("actions")}
                  >
                    <i className="fas fa-chevron-left text-xs" aria-hidden />
                    Back
                  </button>
                )}
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-healthsource-gray hover:bg-healthsource-dark/10"
                  onClick={close}
                  aria-label="Close"
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>

              {step === "actions" ? (
                <div className="mt-5 space-y-3">
                  {multi && (
                    <button
                      type="button"
                      className="w-full rounded-lg border-2 border-[#D5DBE4] bg-white py-3.5 text-center text-base font-bold text-healthsource-dark transition-colors hover:border-healthsource-navy hover:bg-[var(--hs-tint)]"
                      onClick={() => setStep("patients")}
                    >
                      Switch patient
                    </button>
                  )}
                  <button
                    type="button"
                    className="w-full rounded-lg border-2 border-[#D5DBE4] bg-white py-3.5 text-center text-base font-bold text-healthsource-dark transition-colors hover:border-healthsource-navy hover:bg-[var(--hs-tint)]"
                    onClick={() => {
                      onSignOut();
                      close();
                    }}
                  >
                    Log out
                  </button>
                </div>
              ) : (
                patients &&
                patients.length > 0 && (
                  <PatientListPickerContent
                    className="mt-2"
                    patients={patients}
                    getKey={(p) =>
                      `${p.facilityID}-${p.patientID ?? p.id ?? ""}`
                    }
                    onSelect={selectPatient}
                    firstItemRef={firstPatientButtonRef}
                    isItemDisabled={(p) =>
                      String(p.patientID ?? p.id) === currentPatientId
                    }
                  />
                )
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        className="flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
        title="Account"
        aria-label="Open account options"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {initials ? (
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-healthsource-chromeAccent bg-healthsource-navy text-xs font-extrabold"
            aria-hidden
          >
            {initials}
          </span>
        ) : (
          <i className="fas fa-user-circle text-2xl" aria-hidden />
        )}
      </button>
      {modal}
    </div>
  );
}
